import { useEffect, useRef, useState } from "react";
import { Alert, App, Button, Input, Spin, Tag } from "antd";
import {
  Cable,
  Clipboard,
  Download,
  FileText,
  Pencil,
  Save,
  UploadCloud,
  X,
} from "lucide-react";
import { CapabilityGate } from "@/components/deployment/CapabilityGate";
import {
  ssoApi,
  type SsoConfig,
  validateMetadataFile,
} from "@/pages/iam/sso/sso.api";
import "./sso.css";

const EMPTY_CONFIG: SsoConfig = {
  configured: false,
  name: "",
  metadataFileName: "",
  metadataXml: "",
  loginUrl: "",
  spMetadataUrl: "",
  lastTestStatus: null,
};

const TEST_POLL_INTERVAL_MS = 2_000;

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function triggerBlobDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }
      reject(new Error("无法读取 XML 文件"));
    };
    reader.onerror = () => reject(reader.error ?? new Error("无法读取 XML 文件"));
    reader.readAsText(file, "UTF-8");
  });
}

function SsoIntegrationContent() {
  const { message } = App.useApp();
  const uploadRef = useRef<HTMLInputElement>(null);
  const [config, setConfig] = useState<SsoConfig>(EMPTY_CONFIG);
  const [name, setName] = useState("");
  const [metadataFile, setMetadataFile] = useState<File>();
  const [metadataXml, setMetadataXml] = useState<string>();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isReadingFile, setIsReadingFile] = useState(false);
  const [testPassed, setTestPassed] = useState(false);
  const [testId, setTestId] = useState<string>();
  const [passedTestId, setPassedTestId] = useState<string>();

  useEffect(() => {
    let active = true;
    ssoApi
      .getConfig()
      .then((data) => {
        if (!active) return;
        setConfig(data);
        setName(data.name);
        setTestPassed(data.lastTestStatus === "passed");
        setIsEditing(false);
      })
      .catch((error) => {
        if (!active) return;
        message.error(errorMessage(error, "获取 SSO 配置失败"));
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [message]);

  useEffect(() => {
    if (!testId) return;

    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const poll = async () => {
      try {
        const result = await ssoApi.getTestResult(testId);
        if (!active) return;

        if (result.status === "pending") {
          timer = setTimeout(poll, TEST_POLL_INTERVAL_MS);
          return;
        }

        setTestId(undefined);
        setIsTesting(false);
        if (result.status === "passed") {
          setPassedTestId(testId);
          setTestPassed(true);
          message.success("连接测试通过");
          return;
        }

        setTestPassed(false);
        message.error(result.message || (result.status === "expired" ? "连接测试已过期" : "连接测试失败"));
      } catch (error) {
        if (!active) return;
        setTestId(undefined);
        setIsTesting(false);
        setTestPassed(false);
        message.error(errorMessage(error, "查询连接测试结果失败"));
      }
    };

    timer = setTimeout(poll, TEST_POLL_INTERVAL_MS);
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, [message, testId]);

  const currentFileName = metadataFile?.name || config.metadataFileName;
  const visibleLoginUrl = isEditing ? "" : config.loginUrl;
  const visibleSpMetadataUrl = isEditing ? "" : config.spMetadataUrl;
  const formComplete = Boolean(name.trim() && currentFileName);
  const hasChanges = name.trim() !== config.name || Boolean(metadataFile);
  const changedMetadataTestPassed = Boolean(metadataFile && passedTestId && testPassed);
  const canSave = formComplete && testPassed && hasChanges;

  const invalidateTest = () => {
    setTestId(undefined);
    setPassedTestId(undefined);
    setIsTesting(false);
    setTestPassed(false);
  };

  const handleFile = async (file?: File) => {
    if (!file) return;
    const validationError = validateMetadataFile(file);
    if (validationError) {
      message.error(validationError);
      if (uploadRef.current) uploadRef.current.value = "";
      return;
    }

    setIsReadingFile(true);
    try {
      const content = await readFileAsText(file);
      if (!content.trim()) {
        throw new Error("XML 文件内容不能为空");
      }
      setMetadataFile(file);
      setMetadataXml(content);
      invalidateTest();
    } catch (error) {
      message.error(errorMessage(error, "读取 XML 文件失败"));
      if (uploadRef.current) uploadRef.current.value = "";
    } finally {
      setIsReadingFile(false);
    }
  };

  const handleCancel = () => {
    setName(config.name);
    setMetadataFile(undefined);
    setMetadataXml(undefined);
    setTestId(undefined);
    setPassedTestId(undefined);
    setTestPassed(config.lastTestStatus === "passed");
    setIsEditing(false);
    if (uploadRef.current) uploadRef.current.value = "";
  };

  const handleTest = async () => {
    if (!formComplete) {
      message.warning("请填写配置名称并上传 SAML Metadata XML");
      return;
    }
    const currentMetadata = metadataXml || config.metadataXml;
    if (!currentMetadata) {
      message.warning("请上传 SAML Metadata XML");
      return;
    }
    setIsTesting(true);
    try {
      const test = await ssoApi.testConnection(currentMetadata);
      setTestPassed(false);
      setTestId(test.testId);
      window.open(test.loginUrl, "_blank", "noopener,noreferrer");
      message.info("请在新打开的页面中完成 IdP 登录");
    } catch (error) {
      setIsTesting(false);
      setTestPassed(false);
      message.error(errorMessage(error, "连接测试失败"));
    }
  };

  const handleSave = async () => {
    if (!canSave) return;
    setIsSaving(true);
    try {
      const currentMetadata = metadataXml || config.metadataXml;
      await ssoApi.saveConfig({
        name,
        enabled: true,
        metadata: currentMetadata,
        ...(metadataFile && passedTestId ? { testId: passedTestId } : {}),
      });
      const fallbackConfig: SsoConfig = {
        ...config,
        configured: true,
        name: name.trim(),
        metadataFileName: currentFileName,
        metadataXml: currentMetadata,
        lastTestStatus: "passed",
      };
      const nextConfig = await ssoApi.getConfig().catch(() => fallbackConfig);
      setConfig(nextConfig);
      setName(nextConfig.name);
      setMetadataFile(undefined);
      setMetadataXml(undefined);
      setTestId(undefined);
      setPassedTestId(undefined);
      setTestPassed(true);
      setIsEditing(false);
      if (uploadRef.current) uploadRef.current.value = "";
      message.success("SSO 配置已保存");
    } catch (error) {
      message.error(errorMessage(error, "保存 SSO 配置失败"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownload = () => {
    const localXml = metadataXml || config.metadataXml;
    if (!localXml) return;
    triggerBlobDownload(
      new Blob([localXml], { type: "application/xml;charset=utf-8" }),
      currentFileName || "idp-metadata.xml",
    );
  };

  const copyUrl = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      message.success("已复制到剪贴板");
    } catch {
      message.error("复制失败，请手动复制");
    }
  };

  if (isLoading) {
    return (
      <div className="sso-loading" aria-label="正在加载 SSO 配置">
        <Spin />
      </div>
    );
  }

  return (
    <div className="pp-page sso-page">
      <header className="sso-page-header">
        <div className="sso-title-line">
          <h2>SSO 集成</h2>
          <Tag color="purple">AgentPlatformAdmin</Tag>
          <span>对接客户 IdP，作为私有化环境的默认登录方式。</span>
        </div>
        <div className="sso-header-actions">
          {isEditing ? (
            <>
              <Button icon={<X size={16} />} onClick={handleCancel} disabled={isTesting || isSaving}>
                取消
              </Button>
              <Button
                icon={<Cable size={16} />}
                onClick={handleTest}
                loading={isTesting}
                disabled={!formComplete || isSaving || isReadingFile}
              >
                连接测试
              </Button>
              <Button
                type="primary"
                icon={<Save size={16} />}
                onClick={handleSave}
                loading={isSaving}
                disabled={!canSave || isTesting || isReadingFile}
              >
                保存配置
              </Button>
            </>
          ) : (
            <Button type="primary" icon={<Pencil size={16} />} onClick={() => setIsEditing(true)}>
              编辑
            </Button>
          )}
        </div>
      </header>

      <section className="sso-card" aria-labelledby="idp-config-title">
        <div className="sso-card-heading">
          <h3 id="idp-config-title">IdP 配置</h3>
          {testPassed && <Tag color="success">最近测试通过</Tag>}
        </div>

        <label className="sso-field-label" htmlFor="sso-config-name">
          <span aria-hidden="true">*</span> SSO 配置名称
        </label>
        <Input
          id="sso-config-name"
          size="large"
          value={name}
          readOnly={!isEditing}
          disabled={isTesting || isSaving || isReadingFile}
          placeholder="例如：Keycloak"
          maxLength={64}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />

        <p className="sso-upload-help">
          请从身份提供商（IdP）获取 SAML Metadata XML 并上传。仅支持 XML，文件不超过 100 KB。
        </p>
        <div className="sso-file-panel">
          <div className="sso-file-main">
            <span className="sso-file-icon" aria-hidden="true">
              <FileText size={25} />
            </span>
            <div>
              <strong>{currentFileName || "尚未上传 Metadata XML"}</strong>
              <span>{currentFileName ? "XML 文件 · 已上传" : "请选择来自 IdP 的 XML 文件"}</span>
            </div>
          </div>
          <div className="sso-file-actions">
            {currentFileName && (
              <Button icon={<Download size={16} />} onClick={handleDownload}>
                下载文件
              </Button>
            )}
            {isEditing && (
              <Button
                className="sso-upload-button"
                icon={<UploadCloud size={16} />}
                disabled={isTesting || isSaving}
                loading={isReadingFile}
                onClick={() => uploadRef.current?.click()}
              >
                {currentFileName ? "重新上传并覆盖" : "上传 XML 文件"}
              </Button>
            )}
            <input
              ref={uploadRef}
              className="sso-hidden-input"
              type="file"
              accept=".xml,application/xml,text/xml"
              aria-label="上传 SAML Metadata XML"
              onChange={(event) => handleFile(event.target.files?.[0])}
            />
          </div>
        </div>
      </section>

      <section className="sso-card" aria-labelledby="platform-info-title">
        <div className="sso-card-heading sso-platform-heading">
          <div>
            <h3 id="platform-info-title">平台对接信息</h3>
            <p>URL 由当前私有化环境自动生成，无需手动填写。</p>
          </div>
        </div>
        <div className="sso-url-table">
          <div className="sso-url-row">
            <span className="sso-url-label">登录 URL</span>
            <span className="sso-url-value">
              {visibleLoginUrl || "保存后自动展示"}
              {visibleLoginUrl && (
                <button type="button" aria-label="复制登录 URL" onClick={() => copyUrl(visibleLoginUrl)}>
                  <Clipboard size={16} />
                </button>
              )}
            </span>
          </div>
          <div className="sso-url-row">
            <span className="sso-url-label">SP Metadata URL</span>
            <span className="sso-url-value">
              {visibleSpMetadataUrl || "保存后自动展示"}
              {visibleSpMetadataUrl && (
                <button
                  type="button"
                  aria-label="复制 SP Metadata URL"
                  onClick={() => copyUrl(visibleSpMetadataUrl)}
                >
                  <Clipboard size={16} />
                </button>
              )}
            </span>
          </div>
        </div>
      </section>

      {isEditing && (
        changedMetadataTestPassed ? (
          <Alert
            className="sso-save-alert"
            type="success"
            showIcon
            message="连接测试通过，可保存配置"
            description={
              <div className="sso-test-success-details">
                <span>✓ IdP 元数据：已读取登录地址与签名证书</span>
                <span>✓ ACS 回调：ACS 地址可访问</span>
                <span>✓ 字段映射：SAML 标准身份字段可用</span>
                <span>✓ 签名证书：证书签名校验通过</span>
              </div>
            }
          />
        ) : (
          <Alert
            className="sso-save-alert"
            type="info"
            showIcon
            message="保存前需先完成连接测试"
            description="修改配置或重新上传 XML 后，原测试结果失效。"
          />
        )
      )}
    </div>
  );
}

export default function SsoIntegration() {
  return (
    <CapabilityGate capability="iam.ssoConfig" defaultRedirectTo="/iam/users">
      <SsoIntegrationContent />
    </CapabilityGate>
  );
}
